import { Star, Quote, ThumbsUp, Instagram } from "lucide-react";
import { Reveal, SectionTitle } from "./Reveal";
import { REVIEWS, PILLARS } from "@/data/site";
import { FACEBOOK_REVIEWS, INSTAGRAM } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

export const Reviews = () => {
  const { tr } = useLang();
  return (
  <section className="py-24 sm:py-32 bg-[#080B0D]" data-testid="reviews-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <SectionTitle eyebrow={tr("Reviews", "Reseñas")} title={<>Suncare Dental SJDS<br /><span className="text-[#00C9B7]">{tr("100% recommend", "100% recomiendan")}</span></>} />
        <Reveal from="right" className="text-slate-400 flex items-center gap-3">
          <div className="flex text-[#00C9B7]">{[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}</div>
          {tr("Based on the opinion of 5 people", "Basado en la opinión de 5 personas")}
        </Reveal>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.12} className="relative rounded-3xl bg-[#131B21] border border-[#00C9B7]/15 p-8 hover:border-[#00C9B7]/40 transition-colors" data-testid={`review-card-${i}`}>
            <Quote className="text-[#00C9B7]/30 mb-6" size={40} />
            <p className="text-lg text-slate-100 mb-8 leading-relaxed">"{r.text}"</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#00C9B7] text-[#031312] font-bold flex items-center justify-center">{r.name[0]}</div>
                <div><p className="text-sm font-semibold">{r.name}</p><p className="text-xs text-slate-500">{r.date}</p></div>
              </div>
              <span className="text-xs text-[#00C9B7] inline-flex items-center gap-1"><ThumbsUp size={12} /> {tr("Recommends", "Recomienda")}</span>
            </div>
            <a href={FACEBOOK_REVIEWS} target="_blank" rel="noreferrer" className="mt-6 inline-block text-xs uppercase tracking-widest text-slate-400 hover:text-[#00C9B7]" data-testid={`review-read-more-${i}`}>{tr("Read the full review →", "Leer reseña completa →")}</a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export const WhyCora = () => {
  const { tr, pick } = useLang();
  return (
  <section className="py-24 sm:py-32" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionTitle eyebrow={tr("Why Dr. Cora?", "¿Por qué la Dra. Cora?")} title={tr("Care that feels personal.", "Atención que se siente personal.")} />
        <Reveal from="left" delay={0.2} className="mt-10 rounded-3xl overflow-hidden aspect-[4/5]">
          <img src="/img/hero-photo.jpg" alt="Dra. Cora Baltodano at her SunCare Dental clinic" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-testid="why-cora-img" />
        </Reveal>
      </div>
      <div className="lg:col-span-7 flex flex-col justify-center gap-6">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} from="right" delay={i * 0.15} className="group rounded-3xl border border-[#00C9B7]/15 bg-[#131B21] p-8 sm:p-10 hover:bg-[#1A242C] transition-colors" data-testid={`why-pillar-${i}`}>
            <div className="flex gap-6">
              <span className="font-display text-5xl text-[#00C9B7]/40 group-hover:text-[#00C9B7] transition-colors">0{i + 1}</span>
              <div><h3 className="font-display uppercase text-2xl mb-3">{pick(p, "title")}</h3><p className="text-slate-300 leading-relaxed">{pick(p, "text")}</p></div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export const InstagramCta = () => {
  const { tr } = useLang();
  return (
  <section className="relative py-20 overflow-hidden border-y border-[#00C9B7]/10" data-testid="instagram-section">
    <div className="flex whitespace-nowrap marquee font-display uppercase text-6xl sm:text-8xl text-white/[0.04] absolute inset-y-0 items-center">
      {[...Array(2)].map((_, k) => <span key={k} className="pr-10">{tr("@dr._cora · Follow me on Instagram · @dr._cora · Follow me on Instagram ·", "@dr._cora · Sígueme en Instagram · @dr._cora · Sígueme en Instagram ·")}</span>)}
    </div>
    <Reveal from="zoom" className="relative max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
      <div className="flex items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#515bd4] flex items-center justify-center"><Instagram size={30} /></div>
        <div><p className="text-xs uppercase tracking-[0.3em] text-[#00C9B7]">Instagram</p><p className="font-display text-3xl uppercase">{tr("Follow me on Instagram", "Sígueme en Instagram")}</p></div>
      </div>
      <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="rounded-full border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors" data-testid="instagram-follow-btn">@dr._cora</a>
    </Reveal>
  </section>
  );
};
